import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Calendar, User, Briefcase, Languages, CheckCircle, Upload, X } from "lucide-react";
import Footer from "../Components/Footer";
import Navbar from "../Components/Navbar";
import type { Job, UserType } from "../types/job";
import { API_ENDPOINTS } from "../config/api";
import apiClient from "../services/apiClient";
import ApplicationDetailsTable from "../Components/ApplicationDetailsTable";
import type { ApplicationDetailsRow, DownloadUrlResponse } from "../types/application";
import CandidateEvaluationChart from "../Components/CandidateEvaluationChart";
import { useTranslation } from "react-i18next";


function JobDetails() {
    const { jobId } = useParams<{ jobId: string }>();
    const navigate = useNavigate();
    const [job, setJob] = useState<Job | null>(null);
    const [user, setUser] = useState<UserType | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [applications, setApplications] = useState<ApplicationDetailsRow[]>([]);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingApplication, setEditingApplication] = useState<ApplicationDetailsRow | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<string>('');
    const [showDescModal, setShowDescModal] = useState(false);
    const { t } = useTranslation();

    const statusOptions = [t("jobDetails.moveToNextRound"), t("jobDetails.onHold"), t("jobDetails.rejected")];

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) {
                    navigate("/login");
                    return;
                }

                const userRes = await apiClient.get<UserType>(API_ENDPOINTS.ME);
                setUser(userRes.data);

                if (!jobId) throw new Error("Job ID is required");
                const response = await apiClient.get<Job>(API_ENDPOINTS.JOB_BY_ID(jobId));

                setJob(response.data);

                const applicationsRes = await apiClient.get<ApplicationDetailsRow[]>(
                    API_ENDPOINTS.APPLICATION_DETAILS(jobId)
                );

                setApplications(applicationsRes.data);
            } catch (err) {
                console.error("Error fetching data:", err);
                setError("Failed to load job details");
            } finally {
                setLoading(false);
            }
        };

        if (jobId) {
            fetchData();
        }
    }, [jobId, navigate]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <Navbar />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center p-8 bg-white rounded-2xl shadow-xl max-w-sm w-full mx-4 border border-gray-100">
                        <div className="relative mb-6">
                            <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary/20 border-t-primary mx-auto"></div>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{t("jobDetails.loadingTitle")}</h3>
                        <p className="text-gray-500">{t("jobDetails.loadingMessage")}</p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (error || !job) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <Navbar />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center p-10 bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 border border-gray-100">
                        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <span className="text-4xl">⚠️</span>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-3">{t("jobDetails.errorTitle")}</h2>
                        <p className="text-gray-500 mb-8">{error || t("jobDetails.errorMessage")}</p>
                        <Link
                            to="/dashboard"
                            className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-xl hover:bg-blue-800 transition-colors duration-200 font-semibold shadow-lg shadow-primary/30"
                        >
                            <ArrowLeft className="w-5 h-5 mr-2" />
                            {t("jobDetails.backToDashboard")}
                        </Link>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    const skillsArray = (skills: string | undefined) => {
        return skills ? skills.split(",").map(skill => skill.trim()).filter(skill => skill) : [];
    };

    const overviewRows = job ? [
        { label: "Function", value: job.job_function || "N/A" },
        { label: "Primary Language", value: job.job_primary_language || "N/A" },
        { label: "Date Posted", value: formatDate(job.job_created_at) },
        ...(job.job_updated_at ? [{ label: "Last Updated", value: formatDate(job.job_updated_at) }] : [])
    ] : [];

    const handleEditApplication = (application: ApplicationDetailsRow) => {
        setEditingApplication(application);
        setSelectedStatus(application.status);
        setIsEditModalOpen(true);
    };

    const handleSaveEdit = async () => {
        if (!editingApplication || !selectedStatus) return;

        try {
            await apiClient.put(API_ENDPOINTS.APPLICATION_STATUS(editingApplication.application_id), {
                status: selectedStatus
            });

            setApplications(prev => prev.map(app =>
                app.application_id === editingApplication.application_id
                    ? { ...app, status: selectedStatus }
                    : app
            ));

            setIsEditModalOpen(false);
            setEditingApplication(null);
        } catch (error) {
            console.error("Error updating application:", error);
            alert("Failed to update application");
        }
    };

    const handleDeleteApplication = async (applicationId: string) => {
        try {
            await apiClient.delete(API_ENDPOINTS.APPLICATION_BY_ID(applicationId));
            setApplications(prev => prev.filter(app => app.application_id !== applicationId));
        } catch (error) {
            console.error("Error deleting application:", error);
            alert("Failed to delete application");
        }
    };

    const handleDownload = async (row: ApplicationDetailsRow) => {
        try {
            const res = await apiClient.get<DownloadUrlResponse>(
                `/applications/${row.application_id}/download-url`
            );

            window.open(res.data.url, "_blank");

        } catch (error) {
            console.error(error);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 font-sans">
            <Navbar />

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fadeIn">
                <div className="mb-6">
                    <Link
                        to="/dashboard"
                        className="inline-flex items-center text-gray-500 hover:text-primary transition-colors duration-200 group font-medium"
                    >
                        <div className="p-1.5 rounded-lg bg-white border border-gray-200 group-hover:border-primary/50 group-hover:bg-primary/5 mr-2 transition-all">
                            <ArrowLeft className="w-4 h-4" />
                        </div>
                        {t("jobDetails.backToDashboard")}
                    </Link>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
                    <div className="p-6">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide
                                        ${job.job_status === 'active'
                                            ? 'bg-green-100 text-green-700 border border-green-200'
                                            : 'bg-gray-100 text-gray-700 border border-gray-200'}`}>
                                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${job.job_status === 'active' ? 'bg-green-500' : 'bg-gray-500'}`}></span>
                                        {job.job_status || t("jobDetails.unknownStatus")}
                                    </span>
                                </div>

                                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 tracking-tight">
                                    {job.job_title}
                                </h1>

                                <div className="flex flex-wrap items-center gap-3 text-sm font-medium text-gray-600">
                                    <div className="flex items-center text-gray-700">
                                        <Briefcase className="w-4 h-4 mr-1.5 text-primary" />
                                        {job.job_function || "N/A"}
                                    </div>
                                    <span className="hidden sm:inline text-gray-300">|</span>
                                    <div className="flex items-center text-gray-700">
                                        <Languages className="w-4 h-4 mr-1.5 text-primary" />
                                        {job.job_primary_language || "N/A"}
                                    </div>
                                    <span className="hidden sm:inline text-gray-300">|</span>
                                    <div className="flex items-center text-gray-700">
                                        <Calendar className="w-4 h-4 mr-1.5 text-primary" />
                                        {t("jobDetails.postedOn")} {formatDate(job.job_created_at)}
                                    </div>
                                </div>
                            </div>

                            <div className="hidden lg:block shrink-0">
                                <Link
                                    to={`/upload/${job.job_id}`}
                                    state={{ jobDescription: job.job_description, jobTitle: job.job_title, jobId: job.job_id }}
                                    className="inline-flex items-center justify-center px-6 py-3 bg-primary text-white rounded-xl hover:bg-blue-800 transition-all duration-300 font-bold shadow-md hover:shadow-lg transform hover:-translate-y-0.5 text-sm"
                                >
                                    <Upload className="w-4 h-4 mr-2" />
                                    {t("jobDetails.uploadProfiles")}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-8">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                            <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                                    <Briefcase className="w-5 h-5 text-primary mr-3" />
                                    {t("jobDetails.jobDescription")}
                                </h2>
                            </div>

                            <div className="p-8">
                                <div className="prose prose-blue max-w-none text-gray-600 leading-relaxed">
                                    {job.job_description ? (
                                        <p className="whitespace-pre-line">
                                            {job.job_description.length > 25
                                                ? job.job_description.slice(0, 25).trimEnd() + "\u2026"
                                                : job.job_description}

                                            {job.job_description.length > 25 && (
                                                <button
                                                    onClick={() => setShowDescModal(true)}
                                                    className="ml-2 text-primary text-sm font-semibold hover:underline"
                                                >
                                                    {t("jobDetails.readMore")}
                                                </button>
                                            )}
                                        </p>
                                    ) : (
                                        <p className="text-gray-400 italic">
                                            {t("jobDetails.noDescription")}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                            <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                                    <CheckCircle className="w-5 h-5 text-primary mr-3" />
                                    {t("jobDetails.skillsRequirements")}
                                </h2>
                            </div>

                            <div className="p-8 space-y-8">
                                <div>
                                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest mb-4">
                                        {t("jobDetails.mandatorySkills")}
                                    </h3>
                                    <div className="flex flex-wrap gap-2.5">
                                        {skillsArray(job.job_mandatory_skills).map((skill, index) => (
                                            <span
                                                key={index}
                                                className="px-4 py-2 bg-blue-50 text-blue-700 border border-blue-100 rounded-lg text-sm font-semibold"
                                            >
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="border-t border-gray-100"></div>

                                <div>
                                    <h3 className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-4">
                                        {t("jobDetails.preferredSkills")}
                                    </h3>
                                    <div className="flex flex-wrap gap-2.5">
                                        {skillsArray(job.job_optional_skills).map((skill, index) => (
                                            <span
                                                key={index}
                                                className="px-4 py-2 bg-gray-50 text-gray-600 border border-gray-200 rounded-lg text-sm font-medium"
                                            >
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <CandidateEvaluationChart applications={applications} />
                    </div>
                </div>

                <div className="mt-8">
                    <ApplicationDetailsTable
                        rows={applications}
                        onEdit={handleEditApplication}
                        onDelete={handleDeleteApplication}
                        onDownload={handleDownload}
                    />
                </div>
            </main>

            {isEditModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-scaleIn">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-gray-900">{t("jobDetails.editApplication")}</h2>
                            <button
                                type="button"
                                onClick={() => setIsEditModalOpen(false)}
                                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X size={20} className="text-gray-500" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    {t("jobDetails.applicantName")}
                                </label>
                                <input
                                    type="text"
                                    value={editingApplication?.applicant_name || 'Unknown'}
                                    disabled
                                    className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    {t("jobDetails.applicantStatus")}
                                </label>
                                <select
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                                >
                                    <option value="">{t("jobDetails.selectStatus")}</option>
                                    {statusOptions.map((status) => (
                                        <option key={status} value={status}>
                                            {status}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="flex gap-3 p-6 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={() => setIsEditModalOpen(false)}
                                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                            >
                                {t("jobDetails.cancel")}
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveEdit}
                                disabled={!selectedStatus}
                                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-800 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {t("jobDetails.saveChanges")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDescModal && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    onClick={() => setShowDescModal(false)}
                >
                    <div
                        className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 text-gray-900 animate-fadeIn"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                                <Briefcase className="w-4 h-4 text-primary" />
                                {t("jobDetails.jobDescription")}
                            </h3>
                            <button
                                onClick={() => setShowDescModal(false)}
                                className="text-gray-400 hover:text-gray-700 transition-colors text-xl leading-none"
                                aria-label="Close"
                            >
                                &times;
                            </button>
                        </div>
                        <div className="max-h-80 overflow-y-auto pr-1">
                            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                {job.job_description}
                            </p>
                        </div>
                        <div className="flex justify-end mt-5">
                            <button
                                onClick={() => setShowDescModal(false)}
                                className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-blue-800 transition-colors"
                            >
                                {t("jobDetails.close")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
}

export default JobDetails;